import * as React from 'react'

import { Box, colors } from '@factorialco/deprecated-design-system'

import { GoogleMapsContext } from 'modules/core/components/GoogleMaps/GoogleMapsProvider'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import CustomMarkerDiv, {
  CustomMarker,
} from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/ShiftRow/ShiftLocationMap/CustomMarker'
import { MapSelector } from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/ShiftRow/ShiftLocationMap/MapSelector'
import { MapItem } from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/ShiftRow/ShiftLocationMap/types'

interface MapWithCustomMarkersProps {
  employeesMarkers: CustomMarker[]
  workPlacesMarkers: CustomMarker[]
}

const DEFAULT_ZOOM = 17

const DynamicMap = ({ employeesMarkers, workPlacesMarkers }: MapWithCustomMarkersProps) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const { mapsLibraryLoaded, markerLibraryLoaded } = React.useContext(GoogleMapsContext)
  const [map, setMap] = React.useState<google.maps.Map | null>(null)
  const [mapItems, setmapItems] = React.useState<MapItem[]>([])

  useUnsafeEffect(() => {
    if (typeof google === 'undefined') return
    if (!map && mapsLibraryLoaded && markerLibraryLoaded) initMap()
    if (map) drawdMapItems()
  }, [mapsLibraryLoaded, markerLibraryLoaded, map])

  const styles = {
    circleCenter: {
      strokeColor: 'transparent',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#96B2FF',
      fillOpacity: 0.5,
    },
    circleInside: {
      strokeColor: colors.viridian800,
      strokeOpacity: 0.6,
      strokeWeight: 1.5,
      fillColor: 'transparent',
      fillOpacity: 0.3,
    },
    circleIncident: {
      strokeColor: colors.champagne800,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: 'transparent',
      fillOpacity: 0.3,
    },
  }

  const initMap = () => {
    if (!ref.current) return
    const googleMap = new google.maps.Map(ref.current, {
      zoom: DEFAULT_ZOOM,
      mapId: 'map-id',
    })
    setMap(googleMap)
    return googleMap
  }

  const drawdMapItems = () => {
    if (!map) return
    const bounds = new google.maps.LatLngBounds()
    drawLocationMarker(bounds)
    drawEmployeeMarkers(bounds)
    map.fitBounds(bounds)
    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      const mapZoom = map?.getZoom() || DEFAULT_ZOOM
      if (mapZoom > DEFAULT_ZOOM) map.setZoom(DEFAULT_ZOOM)
    })
  }

  const drawLocationMarker = (bounds: google.maps.LatLngBounds) => {
    workPlacesMarkers.map((marker: CustomMarker) => {
      new google.maps.marker.AdvancedMarkerElement({
        map: map,
        position: marker.position,
        content: CustomMarkerDiv({
          avatar: marker.avatar,
          label: marker.label,
        }),
      })
      new google.maps.Circle({
        ...styles.circleCenter,
        map: map,
        center: marker.position,
        radius: marker.radius,
      })
      bounds.extend(marker.position)
    })
  }

  const drawEmployeeMarkers = (bounds: google.maps.LatLngBounds) => {
    const mapItems: MapItem[] = []

    employeesMarkers.map((marker: CustomMarker, index) => {
      const mapMarker = new google.maps.marker.AdvancedMarkerElement({
        map: map,
        position: marker.position,
        content: CustomMarkerDiv({
          avatar: marker.avatar,
          label: marker.label,
          isIncident: marker.isIncident,
        }),
      })

      const circleStyle = marker.isIncident ? styles.circleIncident : styles.circleInside
      const mapCircle = new google.maps.Circle({
        ...circleStyle,
        map: map,
        center: marker.position,
        radius: marker.radius,
      })

      bounds.extend(marker.position)
      mapItems.push({
        marker: mapMarker,
        circle: mapCircle,
        label: marker.label,
        id: index,
        selected: true,
      })
    })
    setmapItems(mapItems)
  }

  const filterMapItems = (visibleMarkerIds: number[]) => {
    if (!mapItems || !map) return

    const newMapItems = [...mapItems]

    newMapItems.forEach((item) => {
      if (visibleMarkerIds.includes(item.id)) {
        item.selected = true
        item.marker.map = map
        item.circle.setMap(map)
      } else {
        item.selected = false
        item.marker.map = null
        item.circle.setMap(null)
      }
    })

    setmapItems(newMapItems)
  }

  return (
    <>
      <MapSelector mapItems={mapItems} onChange={filterMapItems} />
      <Box
        ref={ref}
        height="full"
        padding="s32"
        width="full"
        borderRadius={{ all: 'abs008' }}
      ></Box>
    </>
  )
}

export default DynamicMap
